/**
 * StripePaymentsSettings Component
 * 
 * Allows company admins to connect their Stripe account using Stripe Connect Standard.
 * Once connected, payments will be routed directly to their Stripe account.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useCompanyMembership } from '../../contexts/CompanyMembershipContext';
import { functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { usePermissions } from '../../hooks/usePermissions';

export default function StripePaymentsSettings() {
  const { companyId } = useCompanyMembership();
  const { canManageSettings } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);

  // Load Stripe connection status
  useEffect(() => {
    const loadStripeStatus = async () => {
      if (!companyId) {
        setStatusLoading(false);
        return;
      }

      try {
        setStatusLoading(true);

        // Load from Firestore first (fast)
        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          const stripeData = companyData.stripe || {};
          
          setStripeStatus({
            connected: stripeData.connected || false,
            accountId: stripeData.connectedAccountId || null,
            chargesEnabled: stripeData.chargesEnabled || false,
            payoutsEnabled: stripeData.payoutsEnabled || false,
            detailsSubmitted: stripeData.detailsSubmitted || false,
          });
        } else {
          setStripeStatus({
            connected: false,
            accountId: null,
            chargesEnabled: false,
            payoutsEnabled: false,
            detailsSubmitted: false,
          });
        }

        // Then refresh from Stripe API for accurate status
        try {
          const getStatus = httpsCallable(functions, 'getStripeConnectStatus');
          const result = await getStatus({ companyId });
          setStripeStatus(result.data);
        } catch (statusError) {
          console.warn('[payments] Could not refresh status from Stripe:', statusError);
          // Non-critical, keep Firestore data
        }
      } catch (err) {
        console.error('[payments] Error loading Stripe status:', err);
        setError('Failed to load Stripe connection status');
      } finally {
        setStatusLoading(false);
      }
    };

    loadStripeStatus();
  }, [companyId]);

  const handleConnectStripe = async () => {
    if (!companyId) {
      setError('Company ID not found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const createOnboardingLink = httpsCallable(functions, 'createStripeConnectOnboardingLink');
      const result = await createOnboardingLink({ companyId });

      if (result.data.url) {
        // Redirect to Stripe onboarding
        window.location.href = result.data.url;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err) {
      console.error('[payments] Error creating onboarding link:', err);
      setError(
        err.message || 'Failed to start Stripe connection. Please try again.'
      );
      setLoading(false);
    }
  };

  // Check if user has permission to manage settings
  if (!canManageSettings) {
    return (
      <Alert severity="warning">
        You don't have permission to manage payment settings. Only administrators can connect Stripe accounts.
      </Alert>
    );
  }

  if (statusLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const isConnected = stripeStatus?.connected || false;
  const chargesEnabled = stripeStatus?.chargesEnabled || false;
  const payoutsEnabled = stripeStatus?.payoutsEnabled || false;
  const detailsSubmitted = stripeStatus?.detailsSubmitted || false;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Online Payments (Stripe)
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Connect your Stripe account to accept online payments. Customers will be able to pay invoices directly through the customer portal.
        Payments will be routed to your connected Stripe account.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Paper sx={{ p: 3, mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isConnected && chargesEnabled && payoutsEnabled ? (
          // Connected and fully enabled
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6" color="success.main">
                Stripe Connected
              </Typography>
            </Box>

            <Box display="flex" gap={1} mb={2}>
              <Chip
                icon={<CheckCircleIcon />}
                label="Charges Enabled"
                color="success"
                size="small"
              />
              <Chip
                icon={<CheckCircleIcon />}
                label="Payouts Enabled"
                color="success"
                size="small"
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              Your Stripe account is connected and ready to accept payments. Customers can now pay invoices online through the customer portal.
            </Typography>

            {stripeStatus?.accountId && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Account ID: {stripeStatus.accountId}
              </Typography>
            )}
          </Box>
        ) : detailsSubmitted && (!chargesEnabled || !payoutsEnabled) ? (
          // Submitted but not fully enabled (pending review)
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <CircularProgress size={24} />
              <Typography variant="h6">
                Account Under Review
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" paragraph>
              Your Stripe account details have been submitted and are being reviewed by Stripe.
              This usually takes a few minutes. You'll be able to accept payments once your account is activated.
            </Typography>

            <Button
              variant="outlined"
              onClick={async () => {
                setStatusLoading(true);
                try {
                  const getStatus = httpsCallable(functions, 'getStripeConnectStatus');
                  const result = await getStatus({ companyId });
                  setStripeStatus(result.data);
                } catch (err) {
                  console.error('[payments] Error refreshing status:', err);
                  setError('Failed to refresh status');
                } finally {
                  setStatusLoading(false);
                }
              }}
            >
              Refresh Status
            </Button>
          </Box>
        ) : (
          // Not connected
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <CancelIcon color="disabled" />
              <Typography variant="h6">
                Stripe Not Connected
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" paragraph>
              Connect your Stripe account to enable online payments. You'll be redirected to Stripe to complete the connection process.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              startIcon={<LinkIcon />}
              onClick={handleConnectStripe}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Connecting...
                </>
              ) : (
                'Connect Stripe Account'
              )}
            </Button>
          </Box>
        )}
      </Paper>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2" fontWeight="medium" gutterBottom>
          How it works:
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Click "Connect Stripe Account" to start the onboarding process</li>
            <li>You'll be redirected to Stripe to provide your business information</li>
            <li>Once connected, all online payments will be routed to your Stripe account</li>
            <li>You'll receive payouts according to your Stripe account settings</li>
          </ul>
        </Typography>
      </Alert>
    </Box>
  );
}

